import * as React from 'react';
import { FormattedMessage } from 'react-intl';

import { InfoCircleIcon, CopyToClipboardIcon } from '@notino/react-styleguide';

import { PortalTooltip } from '@components/PortalTooltip';
import { useNewPdDesignEnabled } from '@containers/ProductDetailContainer/ProductDetail/hooks/useNewPdDesignEnabled';
import { useFeatureFlags } from '@context/featureFlags/FeatureFlagsProvider';

import { messages } from '../../messages';
import { useHasStressOnVoucherDiscount } from '../../useHasStressOnVoucherDiscount';
import {
  TextWrapper,
  CodeWrapper,
  DiscountAmmountWrapper,
  TermsWrapper,
  InstructionTextWrapper,
  InfoIconWrapper,
  DiscountInfoDescription,
} from '../styled';

import { StyledInteractiveArea, TooltipContent } from './styled';

interface IProductDiscountProps {
  highlightedDiscountAmount: string | JSX.Element;
  instructionsText: JSX.Element;
  infoTooltipText: JSX.Element;
  discountCode: string;
  highlightedDiscountAmountColor?: string;
}

export const ProductDiscount: React.FC<IProductDiscountProps> = ({
  highlightedDiscountAmount,
  discountCode,
  instructionsText,
  infoTooltipText,
  highlightedDiscountAmountColor,
}) => {
  const newDesign = useNewPdDesignEnabled();
  const { voucherNewDesign } = useFeatureFlags();
  const [showIsCopied, setShowIsCopied] = React.useState(false);
  const tooltipTimeout = React.useRef<NodeJS.Timeout>(null);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(discountCode);
    setShowIsCopied(true);
    clearTimeout(tooltipTimeout.current);
    tooltipTimeout.current = setTimeout(() => setShowIsCopied(false), 2000);
  };

  const isStressed = useHasStressOnVoucherDiscount();

  return (
    <DiscountInfoDescription>
      <TextWrapper
        newDesign={newDesign}
        isStressed={isStressed}
        newVoucherDesign={voucherNewDesign}
      >
        <DiscountAmmountWrapper
          color={highlightedDiscountAmountColor}
          newDesign={newDesign}
          isStressed={isStressed}
        >
          {highlightedDiscountAmount}
        </DiscountAmmountWrapper>

        <span>
          <FormattedMessage
            {...messages.withCode}
            values={{
              code: (
                <CodeWrapper newDesign={newDesign}>{discountCode}</CodeWrapper>
              ),
            }}
          />

          <PortalTooltip
            isOpen={showIsCopied || undefined}
            content={
              <PortalTooltip.Content shouldWrapText={false}>
                {showIsCopied ? (
                  <FormattedMessage {...messages.codeCopied} />
                ) : (
                  <FormattedMessage {...messages.copyCode} />
                )}
              </PortalTooltip.Content>
            }
          >
            <StyledInteractiveArea onClick={handleCopy}>
              <CopyToClipboardIcon color="icon.tertiary" />
            </StyledInteractiveArea>
          </PortalTooltip>
        </span>
      </TextWrapper>

      <TermsWrapper newDesign={newDesign}>
        <InstructionTextWrapper
          newDesign={newDesign}
          newVoucherDesign={voucherNewDesign}
        >
          {instructionsText}

          {infoTooltipText && (
            <PortalTooltip
              content={
                <PortalTooltip.Content>
                  <TooltipContent>{infoTooltipText}</TooltipContent>
                </PortalTooltip.Content>
              }
            >
              <InfoIconWrapper newDesign={newDesign}>
                <InfoCircleIcon
                  width={newDesign ? '13px' : '12px'}
                  height={newDesign ? '13px' : '12px'}
                  color={
                    newDesign && !voucherNewDesign
                      ? 'icon.disabled'
                      : 'icon.tertiary'
                  }
                />
              </InfoIconWrapper>
            </PortalTooltip>
          )}
        </InstructionTextWrapper>
      </TermsWrapper>
    </DiscountInfoDescription>
  );
};
